// src/pages/Community/MemberProfilePage.tsx
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Clapperboard,
  ExternalLink,
  Instagram,
  MapPin,
  Play,
  Star,
  User,
} from 'lucide-react';
import StudioShell from '../../components/Studio/StudioShell';
import { StudioErrorState } from '../../components/Studio/StudioStates';
import {
  AVAILABILITY_LABEL,
  AVAILABILITY_TONE,
  CONTAINER,
  EYEBROW,
  GHOST_BUTTON,
  PANEL,
  PANEL_HOVER,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../components/Studio/studioStyles';
import useStudioResource from '../../hooks/useStudioResource';
import useSEO from '../../hooks/useSEO';
import communityService from '../../services/communityService';
import type { MemberProfile } from '../../types/studio';

const MemberProfilePage = () => {
  const { slug = '' } = useParams<{ slug: string }>();

  const member = useStudioResource<MemberProfile>(
    (signal) => communityService.getMember(slug, signal),
    [slug],
  );

  const data = member.data;

  useSEO({
    title: data
      ? `${data.displayName} — ${data.primaryCraftLabel} at StrymHub`
      : 'Member — StrymHub Community',
    description:
      data?.headline ||
      data?.bio?.slice(0, 160) ||
      'A member of the StrymHub repertory, with their full credits on the platform.',
    keywords: data
      ? `${data.displayName}, ${data.primaryCraftLabel}, strymhub member, ${data.location ?? 'uk'}, cast and crew`
      : 'strymhub member profile',
    type: 'website',
  });

  if (member.loading) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`} role="status" aria-label="Loading member profile">
          <div className="h-4 w-32 animate-pulse rounded bg-white/[0.06]" />
          <div className="mt-8 flex gap-8">
            <div className="h-40 w-40 shrink-0 animate-pulse rounded-2xl bg-white/[0.06]" />
            <div className="flex-1 space-y-4 pt-4">
              <div className="h-10 w-1/2 animate-pulse rounded bg-white/[0.06]" />
              <div className="h-4 w-1/3 animate-pulse rounded bg-white/[0.04]" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-white/[0.04]" />
            </div>
          </div>
          <span className="sr-only">Loading…</span>
        </div>
      </StudioShell>
    );
  }

  if (member.error || !data) {
    return (
      <StudioShell>
        <div className={`${CONTAINER} py-24`}>
          <StudioErrorState
            message={member.error ?? 'We could not find that member.'}
            onRetry={member.reload}
          />
          <div className="mt-8 flex justify-center">
            <Link to="/community/troupe" className={SECONDARY_BUTTON}>
              Back to the troupe
            </Link>
          </div>
        </div>
      </StudioShell>
    );
  }

  const availabilityTone = AVAILABILITY_TONE[data.availability] ?? AVAILABILITY_TONE.UNAVAILABLE;
  const links = [
    data.websiteUrl && { href: data.websiteUrl, label: 'Website', icon: ExternalLink },
    data.instagramUrl && { href: data.instagramUrl, label: 'Instagram', icon: Instagram },
    data.imdbUrl && { href: data.imdbUrl, label: 'IMDb', icon: ExternalLink },
  ].filter(Boolean) as Array<{ href: string; label: string; icon: typeof ExternalLink }>;

  return (
    <StudioShell>
      <div className="relative border-b border-white/10">
        {data.bannerUrl && (
          <>
            <img
              src={data.bannerUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full object-cover opacity-25"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/85 to-[#0B0B0D]/60" />
          </>
        )}

        <div className={`${CONTAINER} relative py-12 sm:py-16`}>
          <Link to="/community/troupe" className={GHOST_BUTTON}>
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            The troupe
          </Link>

          <div className="mt-8 flex flex-col gap-8 sm:flex-row sm:items-end">
            <div className="h-40 w-40 shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-neutral-900">
              {data.avatarUrl ? (
                <img
                  src={data.avatarUrl}
                  alt={data.displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <User className="h-12 w-12 text-neutral-700" aria-hidden="true" />
                </div>
              )}
            </div>

            <div className="min-w-0 flex-1">
              <p className={EYEBROW}>{data.primaryCraftLabel}</p>
              <h1 className="heading-cinematic mt-3 text-3xl text-white sm:text-4xl lg:text-5xl">
                {data.displayName}
              </h1>

              {data.headline && (
                <p className="mt-4 max-w-2xl text-lg leading-relaxed text-neutral-300">
                  {data.headline}
                </p>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full border px-3 py-1 text-xs font-medium ${availabilityTone}`}
                >
                  {AVAILABILITY_LABEL[data.availability] ?? 'Availability unknown'}
                </span>
                {data.foundingMember && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/15 px-3 py-1 text-xs font-medium text-amber-200">
                    <Star className="h-3 w-3" aria-hidden="true" />
                    Founding member
                  </span>
                )}
                {data.location && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-400">
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    {data.location}
                  </span>
                )}
                {data.secondaryCrafts.map((craft) => (
                  <span
                    key={craft}
                    className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-400"
                  >
                    {craft}
                  </span>
                ))}
              </div>

              {(data.showreelUrl || links.length > 0) && (
                <div className="mt-8 flex flex-wrap gap-3">
                  {data.showreelUrl && (
                    <a
                      href={data.showreelUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={PRIMARY_BUTTON}
                    >
                      <Play className="h-4 w-4" aria-hidden="true" />
                      Watch showreel
                    </a>
                  )}
                  {links.map((link) => (
                    <a
                      key={link.label}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={SECONDARY_BUTTON}
                    >
                      <link.icon className="h-4 w-4" aria-hidden="true" />
                      {link.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className={`${CONTAINER} py-16`}>
        <div className="grid gap-14 lg:grid-cols-[1fr_minmax(0,320px)]">
          <div className="space-y-14">
            {data.bio && (
              <section>
                <h2 className="heading-cinematic text-2xl text-white">About</h2>
                <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-neutral-300">
                  {data.bio}
                </p>
              </section>
            )}

            <section>
              <h2 className="heading-cinematic text-2xl text-white">
                StrymHub credits
                {data.credits.length > 0 && (
                  <span className="ml-3 align-middle text-base font-normal text-neutral-500">
                    {data.credits.length}
                  </span>
                )}
              </h2>

              {data.credits.length === 0 ? (
                <div className={`${PANEL} mt-6 px-6 py-10`}>
                  <p className="text-sm leading-relaxed text-neutral-400">
                    No credits on the platform yet. Every member starts here — the first production
                    is the one that changes that.
                  </p>
                  <Link to="/casting" className={`${SECONDARY_BUTTON} mt-6`}>
                    See open calls
                  </Link>
                </div>
              ) : (
                <ul className="mt-6 space-y-4">
                  {data.credits.map((credit) => (
                    <li key={credit.creditId}>
                      <Link
                        to={`/productions/${credit.productionSlug}`}
                        className={`group flex gap-4 p-4 ${PANEL} ${PANEL_HOVER} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
                      >
                        <div className="h-24 w-16 shrink-0 overflow-hidden rounded-lg bg-neutral-900">
                          {credit.productionPosterUrl ? (
                            <img
                              src={credit.productionPosterUrl}
                              alt=""
                              className="h-full w-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <Clapperboard
                                className="h-5 w-5 text-neutral-700"
                                aria-hidden="true"
                              />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="font-display text-base font-bold text-white transition-colors group-hover:text-orange-300">
                            {credit.productionTitle}
                            {credit.productionYear && (
                              <span className="ml-2 text-sm font-normal text-neutral-500">
                                {credit.productionYear}
                              </span>
                            )}
                          </p>
                          <p className="mt-1 text-sm text-neutral-300">
                            {credit.roleName}
                            {credit.lead && (
                              <span className="ml-2 rounded bg-orange-500/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-300">
                                Lead
                              </span>
                            )}
                          </p>
                          <p className="mt-1.5 text-xs uppercase tracking-wider text-neutral-500">
                            {credit.departmentLabel}
                          </p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>

          <aside className="space-y-8">
            {data.skills.length > 0 && (
              <div className={`${PANEL} p-6`}>
                <h2 className={EYEBROW}>Skills</h2>
                <ul className="mt-4 flex flex-wrap gap-2">
                  {data.skills.map((skill) => (
                    <li
                      key={skill}
                      className="rounded-full border border-white/10 px-3 py-1 text-xs text-neutral-300"
                    >
                      {skill}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={`${PANEL} p-6`}>
              <h2 className={EYEBROW}>Casting this person</h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-400">
                {data.displayName.split(' ')[0]} is part of the StrymHub repertory. If you&apos;re
                producing with us, they&apos;re already in the pool.
              </p>
              <Link to="/partners" className={`${SECONDARY_BUTTON} mt-6 w-full`}>
                Produce with StrymHub
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </StudioShell>
  );
};

export default MemberProfilePage;
